import { useEffect, useRef, useState } from 'react';
import { fetchProfilesByIds } from '@/utils/fetchProfilesByIds';
import { formatProfileName } from '@/utils/profileHelpers';
import type { MentionCandidate } from '../mentions/applyMention';
import type { ChatTarget } from '../types';

type ProfileRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  profile_image: string | null;
};

const PROFILE_COLUMNS = 'id, first_name, last_name, profile_image';

/**
 * The people this thread's composer can tag, with the names and avatars the picker shows.
 *
 * Built ON TOP of useChatMemberIds rather than inside it. That hook is on the read-receipt
 * hot path -- it feeds isMessageSeenByAllMembers and re-polls the roster every 30 seconds
 * for every open chat -- and it is deliberately ids-only. Folding a profiles read into it
 * would buy a round trip every 30s per chat for data only the composer needs, and only
 * while someone is actually typing an `@`.
 *
 * So: ids come from there, names are resolved here, and the resolution is skipped whenever
 * the id set has not actually changed, which is what the 30s poll produces almost every
 * time.
 *
 * Direct threads return nothing -- two people in a conversation, nothing to disambiguate.
 */
export function useThreadMentionCandidates(
  target: ChatTarget,
  memberIds: string[],
  currentUserId: string | null,
) {
  const [candidates, setCandidates] = useState<MentionCandidate[]>([]);
  const [failed, setFailed] = useState(false);
  const resolvedKeyRef = useRef<string | null>(null);

  const enabled = target.kind !== 'direct';
  // Sorted, so the 30s poll returning the same people in a different order is a no-op.
  const idsKey = enabled ? [...memberIds].sort().join(',') : '';

  useEffect(() => {
    if (!enabled) {
      setCandidates([]);
      setFailed(false);
      resolvedKeyRef.current = null;
      return;
    }
    if (resolvedKeyRef.current === idsKey) return;

    const ids = idsKey ? idsKey.split(',') : [];
    const taggable = ids.filter((id) => id !== currentUserId);
    if (taggable.length === 0) {
      resolvedKeyRef.current = idsKey;
      setCandidates([]);
      setFailed(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      const { profilesById, failed: readFailed } = await fetchProfilesByIds<ProfileRow>(
        taggable,
        PROFILE_COLUMNS,
        'useThreadMentionCandidates',
      );
      if (cancelled) return;

      // A failed read must not be cached, and must not render as a list of people all
      // called "Volunteer" -- that is the bug fetchProfilesByIds exists to surface.
      if (readFailed) {
        setFailed(true);
        return;
      }

      resolvedKeyRef.current = idsKey;
      setFailed(false);
      setCandidates(
        taggable
          .map((id) => {
            const profile = profilesById[id];
            return {
              id,
              name: formatProfileName(profile?.first_name, profile?.last_name),
              image: profile?.profile_image ?? null,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, idsKey, currentUserId]);

  return { candidates, failed };
}
